import type {
  Credential, Day, Message, NavTarget, Session, TenantBundle, Talent,
} from './types';
import type { Action } from './store';
import type { AppState, Booking, CartItem, CartLine, TierKey, Voucher } from './store';

/**
 * Pure derivations. Everything a screen needs beyond raw content is computed
 * here so the web and native screens stay thin and identical in behaviour.
 */

export const DAY_ABBR: Record<string, string> = { Thu: 'thu', Fri: 'fri', Sat: 'sat', Sun: 'sun' };

/** 'Fri–Sun' -> ['fri','sat','sun']; 'Sat' -> ['sat']. */
export function dayKeysOf(spec: string, order: string[]): string[] {
  const parts = String(spec).split('–').map((x) => x.trim());
  if (parts.length === 2) {
    const a = order.indexOf(DAY_ABBR[parts[0]]);
    const b = order.indexOf(DAY_ABBR[parts[1]]);
    if (a > -1 && b > -1) return order.slice(a, b + 1);
  }
  return parts.map((p) => DAY_ABBR[p]).filter(Boolean);
}

export function dayOrder(bundle: TenantBundle): string[] {
  return bundle.days.map((d) => d.key);
}

export function findDay(bundle: TenantBundle, key: string): Day {
  return bundle.days.find((d) => d.key === key) ?? bundle.days[0];
}

export function findSession(bundle: TenantBundle, id: string | null): Session | null {
  return bundle.sessions.find((s) => s.id === id) ?? null;
}

export function findTalent(bundle: TenantBundle, id: string | null): Talent {
  return bundle.talent.find((t) => t.id === id) ?? bundle.talent[0];
}

export function matches(interests: string[], tags: string[] | undefined): boolean {
  return (tags ?? []).some((t) => interests.includes(t));
}

export function sessionMeta(bundle: TenantBundle, s: Session): string {
  const d = findDay(bundle, s.day);
  const dow = d.dow.charAt(0) + d.dow.slice(1).toLowerCase();
  return `${dow} ${s.t}–${s.end} · ${s.room}`;
}

export function shortMeta(s: Session): string {
  return `${s.t}–${s.end} · ${s.room}`;
}

/** Sessions for the selected day, after the track filter. */
export function filteredSessions(bundle: TenantBundle, state: AppState): Session[] {
  return bundle.sessions.filter(
    (s) => s.day === state.day && (state.track === 'All' || s.track === state.track),
  );
}

/** Sessions on a given day, optionally narrowed to one track, in time order. */
export function filteredSessionsFor(bundle: TenantBundle, day: string, track: string): Session[] {
  return bundle.sessions
    .filter((s) => s.day === day && (track === 'All' || s.track === track))
    .sort((a, b) => minutesOf(a.t) - minutesOf(b.t));
}

export interface SessionGroup {
  label: string;
  items: Session[];
}

/** Morning/Afternoon/Evening, or grouped by room when the view is 'room'. */
export function groupSessions(sessions: Session[], view: 'time' | 'room'): SessionGroup[] {
  const buckets: Record<string, Session[]> = {};
  if (view === 'time') {
    for (const s of sessions) {
      const h = parseInt(s.t, 10);
      const k = h < 12 ? 'Morning' : h < 17 ? 'Afternoon' : 'Evening';
      (buckets[k] ??= []).push(s);
    }
    return ['Morning', 'Afternoon', 'Evening']
      .filter((k) => buckets[k])
      .map((k) => ({ label: k, items: buckets[k] }));
  }
  for (const s of sessions) (buckets[s.room] ??= []).push(s);
  return Object.keys(buckets).sort().map((k) => ({ label: k, items: buckets[k] }));
}

// ── horizontal grid view ──────────────────────────────────────────────────
export const TIMELINE_START_HOUR = 8;
export const TIMELINE_HOURS = 17;
export const PX_PER_MIN = 1.5;

export function minutesOf(hhmm: string): number {
  return parseInt(hhmm.slice(0, 2), 10) * 60 + parseInt(hhmm.slice(3), 10);
}

export const ROOM_ORDER = [
  'Main Stage', 'Grand Ballroom', 'Panel Room 101', 'Panel Room 102', 'Panel Room 103',
  'Panel Room 104', 'Workshop 201', 'Workshop 202', 'Gaming Hall', 'Autograph Row',
  'Photo Op Studio A',
];

export interface TimelineBlock {
  session: Session;
  left: number;
  width: number;
}

export interface TimelineRow {
  room: string;
  blocks: TimelineBlock[];
}

export function timelineWidth(): number {
  return TIMELINE_HOURS * 60 * PX_PER_MIN;
}

export function timelineHours(): { label: string; left: number }[] {
  const out: { label: string; left: number }[] = [];
  for (let h = TIMELINE_START_HOUR; h <= TIMELINE_START_HOUR + TIMELINE_HOURS; h++) {
    const hour = h % 24;
    out.push({
      label: `${hour < 10 ? '0' : ''}${hour}:00`,
      left: (h - TIMELINE_START_HOUR) * 60 * PX_PER_MIN,
    });
  }
  return out;
}

export function timelineRows(daySessions: Session[]): TimelineRow[] {
  const known = ROOM_ORDER.filter((r) => daySessions.some((s) => s.room === r));
  const extra = [...new Set(daySessions.map((s) => s.room))].filter((r) => !ROOM_ORDER.includes(r));
  return [...known, ...extra].map((room) => ({
    room,
    blocks: daySessions
      .filter((s) => s.room === room)
      .map((s) => ({
        session: s,
        left: (minutesOf(s.t) - TIMELINE_START_HOUR * 60) * PX_PER_MIN,
        width: Math.max(112, (minutesOf(s.end) - minutesOf(s.t)) * PX_PER_MIN - 4),
      })),
  }));
}

// ── booking ───────────────────────────────────────────────────────────────
export interface TierMeta {
  key: TierKey;
  label: string;
  note: string;
  price: number;
  room: string;
}

export function tiersFor(talent: Talent): TierMeta[] {
  return [
    { key: 'photo', label: 'Photo op', note: 'Studio print + digital download, 1–2 people', price: talent.prices.photo, room: 'Photo Op Studio A' },
    { key: 'auto', label: 'Autograph', note: 'One item signed, personalisation free', price: talent.prices.auto, room: 'Autograph Row' },
    { key: 'combo', label: 'Combo', note: 'Photo op and autograph in one queue', price: talent.prices.combo, room: 'Photo Op Studio A' },
    { key: 'selfie', label: 'Selfie pass', note: 'Your phone, your shot, 30 seconds', price: talent.prices.selfie, room: 'Autograph Row' },
  ];
}

export interface SlotView {
  time: string;
  left: string;
  soldOut: boolean;
}

/** Deterministic availability so the demo is stable between renders. */
export function slotsFor(bundle: TenantBundle): SlotView[] {
  return bundle.slotTimes.map((time, i) => {
    const soldOut = [0, 3, 6].includes(i);
    return { time, soldOut, left: soldOut ? 'Sold out' : `${2 + (i * 3) % 7} left` };
  });
}

export function cartItemFor(
  bundle: TenantBundle,
  booking: Booking,
): CartItem {
  const talent = findTalent(bundle, booking.talentId);
  const tier = tiersFor(talent).find((t) => t.key === booking.tier)!;
  const dayLabel = findDay(bundle, booking.day).label;
  return {
    kind: 'booking',
    talentId: talent.id,
    title: `${tier.label} — ${talent.name}`,
    price: tier.price,
    slot: booking.slot ?? '',
    dayLabel,
    room: tier.room,
  };
}

export function lineQty(line: CartLine): number {
  return line.kind === 'booking' ? 1 : line.qty;
}

export function lineTotal(line: CartLine): number {
  return line.price * lineQty(line);
}

/**
 * Cart totals.
 *
 * The studio fee applies per booking only — charging it on a t-shirt would be
 * the kind of quiet error nobody catches until an attendee does the arithmetic.
 */
export function cartTotals(bundle: TenantBundle, cart: CartLine[]) {
  const sub = cart.reduce((a, c) => a + lineTotal(c), 0);
  const bookings = cart.filter((c) => c.kind === 'booking').length;
  const fee = bookings * 4;
  const units = cart.reduce((a, c) => a + lineQty(c), 0);
  const cur = bundle.event.currency;

  const lines = [{ label: `Items (${units})`, value: `${cur}${sub}` }];
  if (bookings) lines.push({ label: `Studio fee (${bookings})`, value: `${cur}${fee}` });
  lines.push({ label: 'Handling', value: 'Included' });

  return { sub, fee, total: sub + fee, lines, totalLabel: `${cur}${sub + fee}` };
}

/** Merch and tickets need collecting; only bookings become timed vouchers. */
export function vouchersFromLines(cart: CartLine[]): Voucher[] {
  return cart.map((line, i) => {
    if (line.kind === 'booking') {
      return {
        title: line.title,
        meta: `${line.dayLabel} ${line.slot} · ${line.room}`,
        code: `NC26-${1200 + i}`,
      };
    }
    if (line.kind === 'ticket') {
      return { title: line.title, meta: 'Admission · scan at the doors', code: `ADM-${2400 + i}` };
    }
    return {
      title: line.variantLabel ? `${line.title} (${line.variantLabel})` : line.title,
      meta: 'Merchandise · collect at the info desk',
      code: `MCH-${3600 + i}`,
    };
  });
}

/** Retained for the booking flow; delegates to the union-aware version. */
export function vouchersFromCart(cart: CartLine[]): Voucher[] {
  return vouchersFromLines(cart);
}

/**
 * A ticket purchase issues a credential, which is what turns the Me screen from
 * a prompt into a badge. Returns null when nothing in the cart admits anyone.
 */
export function credentialFromCart(
  bundle: TenantBundle,
  cart: CartLine[],
  holder: string,
): Credential | null {
  const line = cart.find((l): l is Extract<CartLine, { kind: 'ticket' }> => l.kind === 'ticket');
  if (!line) return null;
  const ticket = bundle.tickets?.find((tt) => tt.id === line.ticketId);
  if (!ticket) return null;
  return {
    code: `${bundle.event.id.slice(0, 2).toUpperCase()}26-${ticket.id.toUpperCase()}-${Math.floor(1000 + Math.random() * 8999)}`,
    holder,
    kind: ticket.badgeKind,
    source: 'purchased',
    days: ticket.days,
  };
}

// ── quests ────────────────────────────────────────────────────────────────
export function tierFor(bundle: TenantBundle, points: number) {
  const tiers = bundle.quests.tiers;
  const current = tiers.filter((t) => points >= t.at).slice(-1)[0] ?? tiers[0];
  const next = tiers.find((t) => t.at > points) ?? null;
  const pct = next
    ? Math.min(100, Math.round(((points - current.at) / (next.at - current.at)) * 100))
    : 100;
  return { current, next, pct };
}

// ── header ────────────────────────────────────────────────────────────────
export function headerFor(
  bundle: TenantBundle,
  state: AppState,
): { kicker: string; title: string } {
  const talent = findTalent(bundle, state.talentId);
  const session = findSession(bundle, state.sessionId);
  const { current } = tierFor(bundle, state.points);
  const today = findDay(bundle, state.day);
  const map: Record<string, [string, string]> = {
    home: [`${today.label} September`, 'Your day'],
    schedule: ['Programming', 'Schedule'],
    talent: [`${bundle.event.guestCount} guests`, 'Talent'],
    talentDetail: ['Guest', talent.name],
    events: ['Outside the tracks', 'Special events'],
    hall: [bundle.event.venueName, 'The hall'],
    me: ['Badge & bookings', 'Me'],
    cart: ['Checkout', 'Cart'],
    timeline: [today.label, 'Grid view'],
    quests: [`${state.points} points · ${current.n}`, 'Quests'],
    search: [bundle.event.name, 'Ask anything'],
    sessionDetail: [session ? session.track : 'Programme', session ? session.title : 'Session'],
    journey: [findDay(bundle, state.day).label, 'Build your day'],
    tickets: [bundle.event.dateRange, 'Admission'],
    shop: ['Official merchandise', 'Shop'],
    messages: ['Announcements & updates', 'Inbox'],
  };
  const [kicker, title] = map[state.screen] ?? map.home;
  return { kicker, title };
}

export const TAB_DEFS = [
  { key: 'home', label: 'Today', icon: 'today' },
  { key: 'schedule', label: 'Schedule', icon: 'schedule' },
  { key: 'talent', label: 'Talent', icon: 'talent' },
  { key: 'hall', label: 'Hall', icon: 'hall' },
  { key: 'quests', label: 'Quests', icon: 'quests' },
  { key: 'me', label: 'Me', icon: 'me' },
] as const;

/** Bundle messages plus anything that arrived at runtime, newest first. */
export function allMessages(bundle: TenantBundle, inbox: Message[]): Message[] {
  return [...inbox, ...(bundle.messages ?? [])].sort((a, b) => b.at.localeCompare(a.at));
}

export function unreadCount(bundle: TenantBundle, state: AppState): number {
  return allMessages(bundle, state.inbox).filter((m) => !state.readMessages.includes(m.id)).length;
}

export function activeTab(screen: string): string {
  if (screen === 'talentDetail') return 'talent';
  if (screen === 'events') return 'home';
  if (screen === 'cart') return 'me';
  if (screen === 'timeline' || screen === 'sessionDetail') return 'schedule';
  if (screen === 'journey') return 'schedule';
  if (screen === 'tickets' || screen === 'shop' || screen === 'messages') return 'me';
  if (screen === 'search') return '';
  return screen;
}

/**
 * The list/grid toggle only makes sense where there is a schedule to reshape.
 * Everywhere else it was a dead control taking up header space.
 */
export function showsGridToggle(screen: string): boolean {
  return screen === 'schedule' || screen === 'timeline';
}

/**
 * Which variant to open with.
 *
 * A stored choice always wins, because the picker is an explicit act and
 * overriding it would make the setting look broken. Randomness applies on a
 * first launch — when there is nothing stored yet — and on every launch once
 * the attendee opts into shuffling.
 */
/**
 * Turns a promo's target into the sequence of actions that lands on it.
 * Shared so a card behaves identically on both platforms.
 */
export function navActions(
  target: NavTarget,
  bundle?: TenantBundle,
  currentDay?: string,
): Action[] {
  const out: Action[] = [{ type: 'go', screen: target.screen }];

  if (target.track) {
    // A track filter is scoped to the selected day, and some tracks run on only
    // one of four. Landing on an empty list reads as a broken link, so move to
    // a day that actually has the thing — searching forward from today first,
    // since an attendee is more likely to want what is still ahead of them.
    if (bundle && currentDay) {
      const hasOnDay = (day: string) =>
        bundle.sessions.some((x) => x.day === day && x.track === target.track);
      if (!hasOnDay(currentDay)) {
        const order = bundle.days.map((d) => d.key);
        const from = Math.max(0, order.indexOf(currentDay));
        const rotated = [...order.slice(from), ...order.slice(0, from)];
        const day = rotated.find(hasOnDay);
        if (day) out.push({ type: 'setDay', day });
      }
    }
    out.push({ type: 'setTrack', track: target.track });
  }
  if (target.hallTab) out.push({ type: 'setHallTab', tab: target.hallTab });
  if (target.exZone) out.push({ type: 'setExZone', zone: target.exZone });
  if (target.talentFilter) out.push({ type: 'setTalentFilter', filter: target.talentFilter });
  return out;
}

export function launchThemeId(
  bundle: TenantBundle,
  stored: string | null,
  shuffle: boolean,
  random: () => number = Math.random,
): string | null {
  const variants = bundle.themeVariants ?? [];
  if (!variants.length) return null;
  if (stored && !shuffle) return stored;
  if (stored && shuffle) {
    // Avoid handing back the same look twice running — a shuffle that repeats
    // reads as broken rather than random.
    const others = variants.filter((v) => v.id !== stored);
    const pool = others.length ? others : variants;
    return pool[Math.floor(random() * pool.length)].id;
  }
  return variants[Math.floor(random() * variants.length)].id;
}

/** The talent carousel on Home: guests matching the attendee's interests. */
export function featuredTalent(bundle: TenantBundle, interests: string[]): Talent[] {
  return bundle.talent.filter((t) => matches(interests, t.fandoms)).slice(0, 5);
}

export function nextUp(bundle: TenantBundle, day: string): Session {
  return bundle.sessions.find((s) => s.day === day) ?? bundle.sessions[0];
}

/** Minutes until a session starts, given a clock. Used for "Up next · 18 min". */
export function minutesUntil(bundle: TenantBundle, s: Session, now: Date): number | null {
  const day = bundle.days.find((d) => d.key === s.day);
  if (!day) return null;
  const start = new Date(`${day.date}T${s.t}:00`);
  const diff = Math.round((start.getTime() - now.getTime()) / 60000);
  return Number.isFinite(diff) ? diff : null;
}
